class QuizBuilder {
  constructor(container) {
    this.container = container;
    this.form = container.querySelector('[data-quiz-form]');
    this.questions = container.querySelectorAll('.quiz-question');
    this.currentQuestion = 0;
    this.answers = {};
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.updateProgress();
  }

  setupEventListeners() {
    // Navegação
    this.container.addEventListener('click', (e) => {
      if (e.target.classList.contains('btn-next')) {
        this.nextQuestion();
      } else if (e.target.classList.contains('btn-prev')) {
        this.prevQuestion();
      }
    });

    // Seleção de opções
    this.container.addEventListener('change', (e) => {
      if (e.target.type === 'radio') {
        this.handleOptionChange(e.target);
      }
    });

    // Submit do formulário
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.showResults();
    });
  }

  handleOptionChange(input) {
    const questionNum = input.name.replace('question_', '');
    this.answers[questionNum] = {
      value: input.value,
      tags: input.dataset.tags ? input.dataset.tags.split(',') : [],
      collections: input.dataset.collections ? input.dataset.collections.split(',') : [],
    };

    // Habilitar botão próximo
    const nextBtn = this.questions[this.currentQuestion].querySelector('.btn-next');
    if (nextBtn) {
      nextBtn.disabled = false;
    }
  }

  nextQuestion() {
    if (this.currentQuestion < this.questions.length - 1) {
      this.questions[this.currentQuestion].style.display = 'none';
      this.currentQuestion++;
      this.questions[this.currentQuestion].style.display = 'block';
      this.updateProgress();
    }
  }

  prevQuestion() {
    if (this.currentQuestion > 0) {
      this.questions[this.currentQuestion].style.display = 'none';
      this.currentQuestion--;
      this.questions[this.currentQuestion].style.display = 'block';
      this.updateProgress();
    }
  }

  updateProgress() {
    const progressBar = this.container.querySelector('.progress-fill');
    const progressText = this.container.querySelector('.progress-text');
    const progress = ((this.currentQuestion + 1) / this.questions.length) * 100;

    progressBar.style.width = `${progress}%`;
    progressText.textContent = `Pergunta ${this.currentQuestion + 1} de ${this.questions.length}`;
  }

  async showResults() {
    // Coletar todas as tags e coleções selecionadas
    const allTags = [];
    const allCollections = [];

    Object.values(this.answers).forEach((answer) => {
      allTags.push(...answer.tags);
      allCollections.push(...answer.collections);
    });

    // Remover duplicatas
    const uniqueTags = [...new Set(allTags)];
    const uniqueCollections = [...new Set(allCollections)];

    try {
      const products = await this.fetchRecommendedProducts(uniqueTags, uniqueCollections);
      this.displayResults(products);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
    }
  }

  async fetchRecommendedProducts(tags, collections) {
    try {
      const allProducts = [];

      if (collections.length > 0) {
        // Buscar produtos das coleções especificadas
        for (const collection of collections) {
          try {
            const response = await fetch(
              `${window.Shopify.routes.root}collections/${collection}/products.json?limit=50`
            );
            if (response.ok) {
              const data = await response.json();
              allProducts.push(...data.products);
            }
          } catch (error) {
            console.warn(`Erro ao buscar coleção ${collection}:`, error);
          }
        }

        // Filtrar produtos das coleções pelas tags especificadas
        let filteredProducts = allProducts;
        console.log('tags ', tags);
        if (tags.length > 0) {
          filteredProducts = allProducts.filter((product) =>
            tags.some((tag) => product.tags && product.tags.includes(tag))
          );
        }

        // Se não encontrou produtos com as tags nas coleções, retornar todos os produtos das coleções
        const finalProducts = filteredProducts.length > 0 ? filteredProducts : allProducts;

        // Remover duplicatas baseado no ID
        const uniqueProducts = this.removeDuplicateProducts(finalProducts);

        // Mapear para o formato esperado e limitar a 12 produtos
        return uniqueProducts.slice(0, 12).map((product) => ({
          id: product.id,
          title: product.title,
          url: `/products/${product.handle}`,
          featured_image: product.images && product.images[0] ? product.images[0].src : '',
          price: this.formatPrice(product.variants && product.variants[0] ? product.variants[0].price : ''),
          tags: product.tags || [],
        }));
      } else if (tags.length > 0) {
        // Se não há coleções especificadas, buscar apenas por tags
        const tagProducts = await this.searchProductsByTags(tags);
        return tagProducts.slice(0, 12).map((product) => ({
          id: product.id,
          title: product.title,
          url: product.url || `/products/${product.handle}`,
          featured_image: product.images && product.images[0] ? product.images[0].src : '',
          price: this.formatPrice(product.variants && product.variants[0] ? product.variants[0].price : ''),
          tags: product.tags || [],
        }));
      } else {
        // Se não há nem coleções nem tags, retornar array vazio
        return [];
      }
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      return [];
    }
  }

  async searchProductsByTags(tags) {
    try {
      // Usar a API de busca padrão para tags
      const tagQuery = tags.map((tag) => `tag:${tag}`).join(' OR ');
      const searchParams = new URLSearchParams({
        q: tagQuery,
        type: 'product',
        limit: '20',
      });

      const response = await fetch(`${window.Shopify.routes.root}search?${searchParams}`);
      if (response.ok) {
        const html = await response.text();
        return this.parseProductsFromSearchHTML(html);
      }

      return [];
    } catch (error) {
      console.warn('Erro ao buscar produtos por tags:', error);
      return [];
    }
  }

  parseProductsFromSearchHTML(html) {
    // Criar um documento temporário para parse do HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const products = [];

    // Tentar encontrar elementos de produto com diferentes seletores
    const productSelectors = ['[data-product-id]', '.product-item', '.product-card', '.grid-product', '.card-wrapper'];

    let productElements = [];
    for (const selector of productSelectors) {
      productElements = doc.querySelectorAll(selector);
      if (productElements.length > 0) break;
    }

    productElements.forEach((element) => {
      const productData = this.extractProductDataFromElement(element);
      if (productData) {
        products.push(productData);
      }
    });

    return products;
  }

  extractProductDataFromElement(element) {
    try {
      // Tentar extrair dados do produto do elemento HTML
      const titleSelectors = ['.product-title', '.product__title', '.card__heading', 'h3', 'h2'];
      const linkSelectors = ['a[href*="/products/"]', '.product-link'];
      const imageSelectors = ['img', '.product-image img'];
      const priceSelectors = ['.price', '.product-price', '[data-price]', '.money'];

      let titleElement, linkElement, imageElement, priceElement;

      // Encontrar elementos usando múltiplos seletores
      for (const selector of titleSelectors) {
        titleElement = element.querySelector(selector);
        if (titleElement) break;
      }

      for (const selector of linkSelectors) {
        linkElement = element.querySelector(selector);
        if (linkElement) break;
      }

      for (const selector of imageSelectors) {
        imageElement = element.querySelector(selector);
        if (imageElement) break;
      }

      for (const selector of priceSelectors) {
        priceElement = element.querySelector(selector);
        if (priceElement) break;
      }

      if (!titleElement || !linkElement) {
        return null;
      }

      // Extrair handle do produto da URL
      const handle = linkElement.href.split('/products/')[1]?.split('?')[0];

      return {
        id: element.dataset.productId || `temp-${Date.now()}-${Math.random()}`,
        title: titleElement.textContent.trim(),
        handle: handle,
        url: linkElement.href,
        images: imageElement ? [{ src: imageElement.src }] : [],
        variants: priceElement ? [{ price: this.extractPriceFromText(priceElement.textContent) }] : [],
        tags: (element.dataset.tags || '').split(',').filter(Boolean),
      };
    } catch (error) {
      console.warn('Erro ao extrair dados do produto:', error);
      return null;
    }
  }

  extractPriceFromText(priceText) {
    // Extrair preço do texto, removendo símbolos de moeda
    const cleanPrice = priceText.replace(/[^\d,.-]/g, '');
    const price = parseFloat(cleanPrice.replace(',', '.'));
    return isNaN(price) ? 0 : price;
  }

  removeDuplicateProducts(products) {
    const seen = new Set();
    return products.filter((product) => {
      const identifier = product.id || product.handle || product.url;
      if (seen.has(identifier)) {
        return false;
      }
      seen.add(identifier);
      return true;
    });
  }

  formatPrice(price) {
    if (!price) return '';

    // Converter centavos para reais se necessário
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '';

    const finalPrice = numPrice > 1000 ? numPrice / 100 : numPrice;

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(finalPrice);
  }

  displayResults(products) {
    this.form.style.display = 'none';
    const resultsContainer = this.container.querySelector('.quiz-results');
    const productsContainer = this.container.querySelector('[data-products-container]');

    if (products.length === 0) {
      productsContainer.innerHTML = '<p>Nenhum produto encontrado. Tente ajustar suas respostas.</p>';
    } else {
      productsContainer.innerHTML = products.map((product) => this.createProductCard(product)).join('');
    }

    resultsContainer.style.display = 'block';
  }

  createProductCard(product) {
    return `
      <div class="product-card">
        <a href="${product.url}" class="product-link">
          <div class="product-image">
            <img src="${product.featured_image}" alt="${product.title}" loading="lazy">
          </div>
          <div class="product-info">
            <h3 class="product-title">${product.title}</h3>
            <p class="product-price">${product.price}</p>
          </div>
        </a>
      </div>
    `;
  }
}

// Inicializar quiz quando o DOM estiver pronto
document.addEventListener('DOMContentLoaded', () => {
  const quizContainers = document.querySelectorAll('.quiz-container');
  quizContainers.forEach((container) => {
    new QuizBuilder(container);
  });
});
